import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../../components/layout";
import Spinner from "../../../components/spinner";
import Errors from "../../errors";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import { useMutation } from "@tanstack/react-query";
import {
  getOrderDetails,
  updateOrderDeliveryStatus,
  removeOrderDeliveryStatus,
  setOrderStatus,
  removeOrderStatus,
} from "../../../apis/orders";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function OrderDetails() {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [delivered, setDelivered] = useState(false); // local UI state
  const [deliveryDate, setDeliveryDate] = useState("");
  // Add API delivery status state
  const [apiDelivered, setApiDelivered] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDate, setCancelDate] = useState("");
  const [cancelRemarks, setCancelRemarks] = useState("");
  const [cancelError, setCancelError] = useState(null);
  const [undoCancelError, setUndoCancelError] = useState(null);
  const [returning, setReturning] = useState(false);
  const [returnDate, setReturnDate] = useState("");
  const [returnRemarks, setReturnRemarks] = useState("");
  const [returnError, setReturnError] = useState(null);
  const [undoReturnError, setUndoReturnError] = useState(null);

  useEffect(() => {
    async function fetchOrder() {
      setLoading(true);
      setError(null);
      try {
        const response = await getOrderDetails(order_id);
        const orderData = response.data?.order_data || {};
        setOrder(orderData);
        setOrderItems(response.data?.order_items || []);
        setDelivered(!!orderData.delivery_status); // local UI state
        setApiDelivered(!!orderData.delivery_status); // API state
        setDeliveryDate(orderData.delivery_date || "");
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchOrder();
  }, [order_id]);
  // Delivery status mutation
  const deliveryMutation = useMutation(
    (payload) => updateOrderDeliveryStatus(payload),
    {
      onSuccess: () => {
        window.location.reload();
      },
    }
  );

  const removeDeliveryMutation = useMutation(
    (payload) => removeOrderDeliveryStatus(payload),
    {
      onSuccess: () => {
        window.location.reload();
      },
    }
  );

  // Cancel order mutation
  const cancelOrderMutation = useMutation(
    (payload) => setOrderStatus(payload),
    {
      onSuccess: () => {
        setCancelError(null);
        window.location.reload();
      },
      onError: (err) => {
        let msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to cancel order";
        setCancelError(msg);
      },
    }
  );

  // Undo cancel mutation
  const undoCancelMutation = useMutation(
    (payload) => removeOrderStatus(payload),
    {
      onSuccess: () => {
        setUndoCancelError(null);
        window.location.reload();
      },
      onError: (err) => {
        let msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to undo cancel";
        setUndoCancelError(msg);
      },
    }
  );

  // Return order mutation
  const returnOrderMutation = useMutation(
    (payload) => setOrderStatus(payload),
    {
      onSuccess: () => {
        setReturnError(null);
        window.location.reload();
      },
      onError: (err) => {
        let msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to mark as returned";
        setReturnError(msg);
      },
    }
  );

  // Undo return mutation
  const undoReturnMutation = useMutation(
    (payload) => removeOrderStatus(payload),
    {
      onSuccess: () => {
        setUndoReturnError(null);
        window.location.reload();
      },
      onError: (err) => {
        let msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to undo return";
        setUndoReturnError(msg);
      },
    }
  );

  const handleDeliveryStatusChange = (e) => {
    setDelivered(e.target.checked);
    if (!e.target.checked) {
      setDeliveryDate("");
    }
  };

  const handleDeliveryDateChange = (e) => {
    setDeliveryDate(e.target.value);
  };

  const handleSubmitDeliveryStatus = () => {
    // Always send YYYY-MM-DD to API
    let apiDate = deliveryDate;
    // If value is DD-MM-YYYY, convert to YYYY-MM-DD
    if (
      apiDate &&
      apiDate.includes("-") &&
      apiDate.length === 10 &&
      apiDate.split("-")[0].length === 2
    ) {
      const [dd, mm, yyyy] = apiDate.split("-");
      apiDate = `${yyyy}-${mm}-${dd}`;
    }
    deliveryMutation.mutate({
      order_id,
      delivery_status: delivered,
      delivery_date: delivered ? apiDate : "",
    });
  };

  // Helper to determine which status to show
  const isStandardOrder = order?.order_type === "STANDARD";
  const isCancelled = order?.order_type === "CANCELLED";
  const isReturn = order?.order_type === "RETURN";

  if (loading) return <Spinner />;
  if (error) return <Errors errorObject={error} inline />;

  return (
    <>
      <div className="container_xxl py-8 px-4">
        <div className="flex items-center space-x-4 headingBorder__b mb-6 pb-4">
          <div>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </div>
          <h2 className="text-2xl font-bold">Order Details</h2>
        </div>
        <div className="bg-white rounded-xl shadow p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Order Info */}
          <div>
            <div className="mb-2">
              <span className="font-semibold">Order ID:</span> {order?.order_id}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Invoice No:</span>{" "}
              {order?.final_invoice_no}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Order Date:</span>{" "}
              {order?.ordered_at}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Order Status:</span>{" "}
              {order?.order_status}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Total Amount:</span> ₹
              {order?.total_sum}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Customer Name:</span>{" "}
              {order?.name}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Phone:</span> {order?.phone}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Email:</span> {order?.email}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Payment Method:</span>{" "}
              {order?.payment_method}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Business Customer:</span>{" "}
              {order?.is_business ? "Yes" : "No"}
            </div>
            <div className="mb-2">
              <span className="font-semibold">Invoice:</span>{" "}
              {order?.invoice ? (
                <a
                  href={order?.invoice}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  View Invoice PDF
                </a>
              ) : (
                <span className="text-gray-400">Not available</span>
              )}
            </div>
          </div>
          {/* Address Info */}
          <div>
            <div className="mb-6">
              <div className="font-semibold text-lg mb-2">Billing Address</div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.billing_address?.billing_name}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.billing_address?.billing_email}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.billing_address?.billing_phone}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.billing_address?.billing_address}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.billing_address?.billing_city},{" "}
                {order?.billing_address?.billing_state} -{" "}
                {order?.billing_address?.billing_pincode}
              </div>
            </div>
            <div>
              <div className="font-semibold text-lg mb-2">Shipping Address</div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.shipping_address?.shipping_name}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.shipping_address?.shipping_email}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.shipping_address?.shipping_phone}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.shipping_address?.shipping_address}
              </div>
              <div className="text-gray-700 text-sm mb-1">
                {order?.shipping_address?.shipping_city},{" "}
                {order?.shipping_address?.shipping_state} -{" "}
                {order?.shipping_address?.shipping_pincode}
              </div>
            </div>
          </div>
        </div>
        {/* Order Items Section */}
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Order Items</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-left">Quantity</th>
                  <th className="px-4 py-2 text-left">Unit Value</th>
                  <th className="px-4 py-2 text-left">Price</th>
                  <th className="px-4 py-2 text-left">Tax</th>
                  <th className="px-4 py-2 text-left">Total</th>
                  <th className="px-4 py-2 text-left">Image</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="px-4 py-2">{item.name}</td>
                    <td className="px-4 py-2">{item.quantity}</td>
                    <td className="px-4 py-2">
                      {item.each_unit_value} {item.price_per_unit}
                    </td>
                    <td className="px-4 py-2">₹{item.per_piece_price_rate}</td>
                    <td className="px-4 py-2">{item.tax_slab}</td>
                    <td className="px-4 py-2">₹{item.product_total_amt}</td>
                    <td className="px-4 py-2">
                      {item.media_url ? (
                        <img
                          src={item.media_url}
                          alt={item.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <span className="text-gray-400">No Image</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex gap-4 mb-8">
          {/* <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/orders/${order_id}/pdf`)}
          >
            View PDF
          </Button> */}
          {order?.invoice && (
            <a
              href={order?.invoice}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline text-blue-600"
            >
              Download Invoice
            </a>
          )}
        </div>

        {/* Delivery Status Section */}
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Delivery Status</h3>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="font-semibold w-32">Status:</span>
              <span
                className={
                  isStandardOrder
                    ? delivered
                      ? "text-green-600 font-bold"
                      : "text-red-600 font-bold"
                    : isCancelled
                    ? "text-red-600 font-bold"
                    : isReturn
                    ? "text-yellow-600 font-bold"
                    : "text-yellow-600 font-bold"
                }
              >
                {isStandardOrder
                  ? delivered
                    ? "Delivered"
                    : "Not Delivered"
                  : order?.order_type}
              </span>
            </div>
            {/* Show delivery date if delivered and STANDARD */}
            {isStandardOrder && delivered && (
              <div className="flex items-center gap-3">
                <span className="font-semibold w-32">Delivery Date:</span>
                <TextField
                  type="date"
                  size="small"
                  value={(() => {
                    if (!deliveryDate) return "";
                    if (
                      deliveryDate.includes("-") &&
                      deliveryDate.length === 10 &&
                      deliveryDate.split("-")[0].length === 2
                    ) {
                      const [dd, mm, yyyy] = deliveryDate.split("-");
                      return `${yyyy}-${mm}-${dd}`;
                    }
                    return deliveryDate;
                  })()}
                  onChange={handleDeliveryDateChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
              </div>
            )}
            {/* Show cancel date and remarks if CANCELLED */}
            {!isStandardOrder && isCancelled && (
              <>
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Cancel Date:</span>
                  <span>
                    {order?.order_status_date
                      ? order.order_status_date
                      : "-"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Remarks:</span>
                  <span>
                    {order?.order_status_remarks
                      ? order.order_status_remarks
                      : "-"}
                  </span>
                </div>
                {/* Undo Cancel Button */}
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setUndoCancelError(null);
                      undoCancelMutation.mutate({
                        order_id,
                        order_status: "CANCELLED",
                      });
                    }}
                    disabled={undoCancelMutation.isLoading}
                  >
                    Undo Cancel
                  </Button>
                  {undoCancelError && (
                    <span className="text-red-600 text-sm">{undoCancelError}</span>
                  )}
                </div>
              </>
            )}
            {/* Show return date and remarks if RETURN */}
            {!isStandardOrder && isReturn && (
              <>
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Delivery Date:</span>
                  <span>
                    {order?.delivery_date
                      ? order.delivery_date
                      : "-"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Return Date:</span>
                  <span>
                    {order?.order_status_date
                      ? order.order_status_date
                      : "-"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Remarks:</span>
                  <span>
                    {order?.order_status_remarks
                      ? order.order_status_remarks
                      : "-"}
                  </span>
                </div>
                {/* Undo Return Button */}
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setUndoReturnError(null);
                      undoReturnMutation.mutate({
                        order_id,
                        order_status: "RETURN",
                      });
                    }}
                    disabled={undoReturnMutation.isLoading}
                  >
                    Undo Return
                  </Button>
                  {undoReturnError && (
                    <span className="text-red-600 text-sm">{undoReturnError}</span>
                  )}
                </div>
              </>
            )}
            {/* Action buttons */}
            <div className="flex items-center gap-3 mt-2">
              {isStandardOrder && (
                <>
                  <Button
                    variant="contained"
                    color={delivered ? "warning" : "success"}
                    onClick={() => {
                      if (delivered) {
                        setDelivered(false);
                        setDeliveryDate("");
                        // Only call API if deliveryDate is set
                        if (deliveryDate) {
                          removeDeliveryMutation.mutate({ order_id });
                        }
                        // else just revert UI, do not call API
                      } else {
                        setDelivered(true);
                      }
                    }}
                    disabled={
                      deliveryMutation.isLoading ||
                      removeDeliveryMutation.isLoading ||
                      cancelling ||
                      returning
                    }
                  >
                    {delivered ? "Remove Delivery Status" : "Mark as Delivered"}
                  </Button>
                  {/* Mark as Cancelled button */}
                  {!delivered && !cancelling && !returning && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setCancelling(true)}
                      disabled={cancelOrderMutation.isLoading}
                    >
                      Mark as Cancelled
                    </Button>
                  )}
                  {/* Save delivery date button */}
                  {isStandardOrder && delivered && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleSubmitDeliveryStatus}
                      disabled={!deliveryDate || deliveryMutation.isLoading}
                    >
                      Save Delivery Date
                    </Button>
                  )}
                  {/* Mark as Returned button (AFTER Save Delivery Date) */}
                  {/* Only enable if API says delivered and not in returning/cancelling */}
                  {apiDelivered && !returning && !cancelling && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setReturning(true)}
                      disabled={returnOrderMutation.isLoading || !deliveryDate}
                    >
                      Mark as Returned
                    </Button>
                  )}
                </>
              )}
            </div>
            {/* Cancel form */}
            {isStandardOrder && cancelling && !delivered && (
              <div className="flex flex-col gap-3 mt-4 border-t pt-4">
                {/* Show cancel error if any */}
                {cancelError && (
                  <div className="text-red-600 text-sm">{cancelError}</div>
                )}
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Cancel Date:</span>
                  <TextField
                    type="date"
                    size="small"
                    value={cancelDate}
                    onChange={e => setCancelDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Remarks:</span>
                  <TextField
                    size="small"
                    value={cancelRemarks}
                    onChange={e => setCancelRemarks(e.target.value)}
                    placeholder="Enter remarks"
                    sx={{ minWidth: 250 }}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setCancelError(null);
                      if (!cancelDate) return;
                      cancelOrderMutation.mutate({
                        order_id,
                        order_status: "CANCELLED",
                        order_status_date: cancelDate,
                        order_status_remarks: cancelRemarks,
                      });
                    }}
                    disabled={
                      !cancelDate || cancelOrderMutation.isLoading
                    }
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setCancelling(false);
                      setCancelError(null);
                    }}
                    disabled={cancelOrderMutation.isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {/* Return form */}
            {isStandardOrder && delivered && returning && (
              <div className="flex flex-col gap-3 mt-4 border-t pt-4">
                {/* Show return error if any */}
                {returnError && (
                  <div className="text-red-600 text-sm">{returnError}</div>
                )}
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Return Date:</span>
                  <TextField
                    type="date"
                    size="small"
                    value={returnDate}
                    onChange={e => setReturnDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-32">Remarks:</span>
                  <TextField
                    size="small"
                    value={returnRemarks}
                    onChange={e => setReturnRemarks(e.target.value)}
                    placeholder="Enter remarks"
                    sx={{ minWidth: 250 }}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setReturnError(null);
                      if (!returnDate) return;
                      returnOrderMutation.mutate({
                        order_id,
                        order_status: "RETURN",
                        order_status_date: returnDate,
                        order_status_remarks: returnRemarks,
                      });
                    }}
                    disabled={
                      !returnDate || returnOrderMutation.isLoading
                    }
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setReturning(false);
                      setReturnError(null);
                    }}
                    disabled={returnOrderMutation.isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            <div className="text-xs text-gray-500 mt-2">
              {isStandardOrder
                ? delivered
                  ? returning
                    ? "Set return date and remarks, then click Save."
                    : "You can update the delivery date, mark as not delivered, or mark as returned."
                  : cancelling
                  ? "Set cancel date and remarks, then click Save."
                  : "Click 'Mark as Delivered' to set delivery status and date, or 'Mark as Cancelled' to cancel the order."
                : isCancelled
                ? "Order has been cancelled."
                : isReturn
                ? "Order has been returned."
                : ""}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OrderDetails;
